import { t } from "@lingui/core/macro";
import {
  ANNOTATION_COMMENT_MAX_LENGTH,
  ANNOTATION_EXCERPT_TEXT_MAX_LENGTH,
  type Annotation,
  type AnnotationShape,
} from "@rakazo/contracts";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
} from "@rakazo/ui-web";
import {
  Check,
  ChevronDown,
  Circle,
  Highlighter,
  Minus,
  MousePointer2,
  MoveUpRight,
  PenLine,
  Plus,
  Square,
  Undo2,
} from "lucide-react";
import {
  type KeyboardEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { cropExcerptPng } from "../../lib/annotation-excerpt";
import {
  commentAnchor,
  excerptRect,
  HIGHLIGHT_COLOR,
  MARKUP_COLORS,
  shapeBounds,
} from "../../lib/annotation-shapes";
import {
  defaultSelection,
  type MarkupDraft,
  normalizeComment,
  pollPreview,
  reconcileSelection,
  splitAnnotations,
  toggleSelection,
} from "../../lib/markup-session";
import {
  openPdf,
  type PdfPageSize,
  type RelativeRect,
  readPageSizes,
  readTextInRect,
  startPageDraw,
} from "../../lib/pdf-render";
import { clipUtf16, utf16Length } from "../../lib/utf16";
import { IconTooltip } from "../IconTooltip";
import { type DrawPage, MarkupPage, type PageMark, type PageTool } from "./MarkupPage";
import type { MarkupClient, MarkupExcerpt } from "./markup-client";

/** An opened PDF as the view needs it: page sizes, drawing a page and reading text under a box. */
export type OpenedPdf = {
  pages: PdfPageSize[];
  draw: DrawPage;
  readText: (page: number, rect: RelativeRect) => Promise<string>;
  close: () => void;
};

/** Opens PDF bytes; swapped in tests so they run without pdf.js. */
export type PdfOpener = (bytes: Uint8Array) => Promise<OpenedPdf>;

/**
 * Opens PDF bytes with pdf.js, hardened as in lib/pdf-render (canvas only, no scripts, no links);
 * each page draw can be cancelled.
 */
export const openWithPdfJs: PdfOpener = async (bytes) => {
  const doc = await openPdf(bytes);
  const pages = await readPageSizes(doc);
  return {
    pages,
    draw: (page, canvas, cssWidth) => startPageDraw(() => doc.getPage(page), canvas, cssWidth),
    readText: async (page, rect) => readTextInRect(await doc.getPage(page), rect),
    close: () => void doc.loadingTask.destroy(),
  };
};

type Phase =
  | { kind: "loading" }
  | { kind: "failed"; reason?: string; timedOut?: boolean }
  | { kind: "unsupported" }
  | { kind: "ready"; pdf: OpenedPdf };

/** Open comment popover: a fresh drawing or an existing mark. */
type Editing =
  | { kind: "draft"; draft: MarkupDraft }
  | { kind: "mark"; id: string; comment: string };

/** Excerpt prepared for the check step before sending. */
type PreparedExcerpt = MarkupExcerpt & { label: string; comment: string };

const ZOOM_STEPS = [0.5, 0.75, 1, 1.25, 1.5, 2] as const;
const PAGE_GAP_PX = 16;
const MAX_FIT_WIDTH_PX = 900;
/** Pause after the last size change of the view before the pages redraw at the new width. */
export const FIT_WIDTH_SETTLE_MS = 100;

function toolLabel(tool: PageTool): string {
  switch (tool) {
    case "select":
      return t`Select`;
    case "rect":
      return t`Rectangle`;
    case "ellipse":
      return t`Ellipse`;
    case "arrow":
      return t`Arrow`;
    case "freehand":
      return t`Freehand`;
    case "highlight":
      return t`Highlighter`;
  }
}

const TOOL_ICONS: Record<PageTool, ReactNode> = {
  select: <MousePointer2 className="size-4" aria-hidden="true" />,
  rect: <Square className="size-4" aria-hidden="true" />,
  ellipse: <Circle className="size-4" aria-hidden="true" />,
  arrow: <MoveUpRight className="size-4" aria-hidden="true" />,
  freehand: <PenLine className="size-4" aria-hidden="true" />,
  highlight: <Highlighter className="size-4" aria-hidden="true" />,
};

const TOOLS: readonly PageTool[] = ["select", "rect", "ellipse", "arrow", "freehand", "highlight"];

const iconButton =
  "inline-grid size-8 shrink-0 place-items-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring disabled:pointer-events-none disabled:opacity-40 aria-pressed:bg-muted aria-pressed:text-foreground";

/**
 * PDF preview of one artifact version with markup: pages on canvas, a toolbar to draw marks,
 * a comment field at each mark, a list of all marks and a check step before the chosen marks go
 * to the bot that made the file. Drafts live only here, so reloading the list never touches them.
 */
export function PdfMarkupView({
  artifactId,
  name,
  botName,
  userId,
  client,
  openPdfBytes = openWithPdfJs,
  onSubmitted,
}: {
  artifactId: string;
  name: string;
  botName?: string;
  userId: string;
  client: MarkupClient;
  openPdfBytes?: PdfOpener;
  onSubmitted?: (result: { botId: string; threadId: string; messageId: string }) => void;
}) {
  const [phase, setPhase] = useState<Phase>({ kind: "loading" });
  const [reloadKey, setReloadKey] = useState(0);
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [listError, setListError] = useState(false);
  const [tool, setTool] = useState<PageTool>("rect");
  const [color, setColor] = useState<string>(MARKUP_COLORS[0]);
  const [zoomIndex, setZoomIndex] = useState(2);
  const [fitWidth, setFitWidth] = useState(0);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [created, setCreated] = useState<string[]>([]);
  const [prepared, setPrepared] = useState<PreparedExcerpt[] | null>(null);
  const [preparing, setPreparing] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const viewRef = useRef<HTMLDivElement>(null);
  const canvases = useRef(new Map<number, HTMLCanvasElement>());
  const previousCurrent = useRef<Annotation[] | null>(null);

  const split = useMemo(() => splitAnnotations(annotations, artifactId), [annotations, artifactId]);
  const labels = useMemo(
    () => new Map(split.current.map((a, i) => [a.id, String(i + 1)])),
    [split.current],
  );
  const openCurrent = split.current.filter((a) => a.status === "open");
  const chosen = openCurrent.filter((a) => selected.has(a.id));
  const sender = botName ?? t`the bot`;

  const applyList = useCallback(
    (list: Annotation[]) => {
      const current = splitAnnotations(list, artifactId).current;
      const previous = previousCurrent.current;
      setSelected((sel) =>
        previous ? reconcileSelection(sel, previous, current) : defaultSelection(current),
      );
      previousCurrent.current = current;
      setAnnotations(list);
    },
    [artifactId],
  );

  const reloadList = useCallback(
    async (signal?: AbortSignal) => {
      try {
        applyList(await client.list(artifactId, signal));
        setListError(false);
      } catch {
        if (!signal?.aborted) setListError(true);
      }
    },
    [applyList, artifactId, client],
  );

  useEffect(() => {
    const abort = new AbortController();
    let opened: OpenedPdf | null = null;
    setPhase({ kind: "loading" });
    previousCurrent.current = null;
    void reloadList(abort.signal);
    (async () => {
      try {
        const { preview, timedOut } = await pollPreview(
          () => client.preview(artifactId, abort.signal),
          abort.signal,
        );
        if (preview.status === "unsupported") return setPhase({ kind: "unsupported" });
        if (preview.status !== "ready") {
          return setPhase({ kind: "failed", reason: preview.error, timedOut });
        }
        const bytes = await client.previewPdf(artifactId, abort.signal);
        opened = await openPdfBytes(bytes);
        if (abort.signal.aborted) return opened.close();
        setPhase({ kind: "ready", pdf: opened });
      } catch {
        if (!abort.signal.aborted) setPhase({ kind: "failed" });
      }
    })();
    return () => {
      abort.abort();
      opened?.close();
    };
  }, [artifactId, client, openPdfBytes, reloadList, reloadKey]);

  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    const measure = () =>
      setFitWidth(Math.max(0, Math.min(MAX_FIT_WIDTH_PX, view.clientWidth - 2 * PAGE_GAP_PX)));
    measure();
    if (typeof ResizeObserver === "undefined") return;
    // Dragging the split divider resizes the view on every frame; pages redraw once it rests.
    let timer: ReturnType<typeof setTimeout> | undefined;
    const observer = new ResizeObserver(() => {
      clearTimeout(timer);
      timer = setTimeout(measure, FIT_WIDTH_SETTLE_MS);
    });
    observer.observe(view);
    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [phase.kind]);

  const pages = phase.kind === "ready" ? phase.pdf.pages : [];
  const widest = Math.max(1, ...pages.map((p) => p.width));
  const zoom = ZOOM_STEPS[zoomIndex] ?? 1;
  const pageSize = (page: number) => {
    const p = pages[page - 1] ?? { width: 1, height: 1 };
    const width = Math.round(fitWidth * zoom * (p.width / widest));
    return { width, height: Math.round((width * p.height) / p.width) };
  };

  const onCanvas = useCallback((page: number, canvas: HTMLCanvasElement | null) => {
    if (canvas) canvases.current.set(page, canvas);
    else canvases.current.delete(page);
  }, []);

  const onDraw = useCallback((page: number, shape: AnnotationShape) => {
    setEditError(null);
    setEditing({ kind: "draft", draft: { page, shape, comment: "" } });
  }, []);

  const pick = useCallback(
    (id: string) => {
      const mark = annotations.find((a) => a.id === id);
      if (!mark) return;
      setEditError(null);
      setEditing({ kind: "mark", id, comment: mark.comment });
    },
    [annotations],
  );

  const shapeColor = (kind: PageTool) => (kind === "highlight" ? HIGHLIGHT_COLOR : color);

  const marksFor = (page: number): PageMark[] => {
    const marks: PageMark[] = split.current
      .filter((a) => a.page === page)
      .map((a) => ({
        id: a.id,
        label: labels.get(a.id) ?? "",
        shape: a.shape,
        done: a.status === "done",
        active: editing?.kind === "mark" && editing.id === a.id,
      }));
    if (editing?.kind === "draft" && editing.draft.page === page) {
      marks.push({
        id: "draft",
        label: String(split.current.length + 1),
        shape: editing.draft.shape,
        done: false,
        active: true,
      });
    }
    return marks;
  };

  const saveEditing = async () => {
    if (!editing) return;
    setSaving(true);
    setEditError(null);
    try {
      if (editing.kind === "draft") {
        const made = await client.create({
          artifactId,
          page: editing.draft.page,
          shape: editing.draft.shape,
          comment: normalizeComment(editing.draft.comment),
        });
        setCreated((list) => [...list, made.id]);
        setSelected((sel) => new Set(sel).add(made.id));
        applyList([...annotations, made]);
      } else {
        const updated = await client.update({
          id: editing.id,
          comment: normalizeComment(editing.comment),
        });
        applyList(annotations.map((a) => (a.id === updated.id ? updated : a)));
      }
      setEditing(null);
    } catch {
      setEditError(t`Could not save. Your text is still here; try again.`);
    } finally {
      setSaving(false);
    }
  };

  const setMarkStatus = async (id: string, next: "open" | "done") => {
    setEditError(null);
    try {
      const updated = await client.update({ id, status: next });
      applyList(annotations.map((a) => (a.id === updated.id ? updated : a)));
      setEditing(null);
    } catch {
      setEditError(t`Could not change the status. Try again.`);
    }
  };

  const removeMark = async (id: string) => {
    setEditError(null);
    try {
      await client.remove(id);
      setCreated((list) => list.filter((x) => x !== id));
      applyList(annotations.filter((a) => a.id !== id));
      setEditing(null);
    } catch {
      setEditError(t`Could not delete the mark. Try again.`);
    }
  };

  const undo = () => {
    if (editing?.kind === "draft") return setEditing(null);
    const last = created.at(-1);
    if (last) void removeMark(last);
  };

  const prepare = async () => {
    if (phase.kind !== "ready" || chosen.length === 0) return;
    setPreparing(true);
    setSendError(null);
    const out: PreparedExcerpt[] = [];
    for (const a of chosen) {
      const size = pageSize(a.page);
      const rect = excerptRect(a.shape, size);
      let canvas = canvases.current.get(a.page);
      if (!canvas) {
        canvas = document.createElement("canvas");
        try {
          await phase.pdf.draw(a.page, canvas, size.width);
        } catch {
          canvas = undefined;
        }
      }
      const png = canvas ? (cropExcerptPng(canvas, rect) ?? "") : "";
      let text = "";
      try {
        // Text only from under the shape itself; the excerpt margin would pull in neighbouring lines.
        text = clipUtf16(
          await phase.pdf.readText(a.page, shapeBounds(a.shape)),
          ANNOTATION_EXCERPT_TEXT_MAX_LENGTH,
        );
      } catch {
        text = "";
      }
      out.push({
        annotationId: a.id,
        png,
        text,
        label: labels.get(a.id) ?? "",
        comment: a.comment,
      });
    }
    setPreparing(false);
    setPrepared(out);
  };

  const send = async () => {
    if (!prepared) return;
    setSending(true);
    setSendError(null);
    try {
      const result = await client.submit({
        artifactId,
        annotationIds: prepared.map((p) => p.annotationId),
        excerpts: prepared.map(({ annotationId, png, text }) => ({ annotationId, png, text })),
      });
      setPrepared(null);
      setStatus(t`Sent to ${sender}. The new version appears in the chat.`);
      onSubmitted?.(result);
    } catch {
      setSendError(t`Could not send. Nothing was sent; try again.`);
    } finally {
      setSending(false);
    }
  };

  const scrollToPage = (page: number) => {
    viewRef.current
      ?.querySelector(`[data-page="${page}"]`)
      ?.scrollIntoView?.({ block: "center", behavior: "smooth" });
  };

  if (phase.kind === "loading") {
    return (
      <Centered>
        <p role="status" className="text-[13.5px] text-muted-foreground">
          {t`Preparing preview…`}
        </p>
      </Centered>
    );
  }
  if (phase.kind === "unsupported") {
    return (
      <Centered>
        <p className="text-[13.5px] text-muted-foreground">
          {t`This file type has no preview. Download it to open it.`}
        </p>
      </Centered>
    );
  }
  if (phase.kind === "failed") {
    return (
      <Centered>
        <p role="alert" className="max-w-[320px] text-center text-[13.5px] text-muted-foreground">
          {phase.timedOut
            ? t`The preview is taking too long.`
            : (phase.reason ?? t`The preview could not be created.`)}
        </p>
        <Button variant="outline" size="sm" onClick={() => setReloadKey((k) => k + 1)}>
          {t`Try again`}
        </Button>
      </Centered>
    );
  }

  const doneCount = split.current.length - openCurrent.length;

  return (
    <div className="flex h-full min-h-0 flex-col bg-background">
      <div
        role="toolbar"
        aria-label={t`Markup tools`}
        className="flex shrink-0 flex-wrap items-center gap-x-1 border-b border-border px-2 py-1.5"
      >
        {/* Two groups: a narrow panel wraps zoom and Umsetzen onto a second row instead of
            scrolling them out of sight (the split allows the panel down to 320 px). */}
        <div className="flex min-w-0 max-w-full items-center overflow-x-auto">
          {TOOLS.map((item) => (
            <IconTooltip key={item} label={toolLabel(item)}>
              <button
                type="button"
                className={iconButton}
                aria-label={toolLabel(item)}
                aria-pressed={tool === item}
                onClick={() => setTool(item)}
              >
                {TOOL_ICONS[item]}
              </button>
            </IconTooltip>
          ))}
          <span className="mx-1 h-5 w-px shrink-0 bg-border" aria-hidden="true" />
          {MARKUP_COLORS.map((c) => (
            <button
              key={c}
              type="button"
              className="grid size-6 shrink-0 place-items-center rounded-full focus-visible:outline-2 focus-visible:outline-ring disabled:opacity-40"
              aria-label={colourLabel(c)}
              title={colourLabel(c)}
              aria-pressed={color === c}
              disabled={tool === "highlight" || tool === "select"}
              onClick={() => setColor(c)}
            >
              <span
                className={`size-4 rounded-full ${color === c ? "ring-2 ring-foreground ring-offset-2 ring-offset-background" : ""}`}
                style={{ background: c }}
              />
            </button>
          ))}
        </div>
        <div className="flex grow items-center">
          <IconTooltip label={t`Undo`}>
            <button
              type="button"
              className={iconButton}
              aria-label={t`Undo`}
              disabled={editing?.kind !== "draft" && created.length === 0}
              onClick={undo}
            >
              <Undo2 className="size-4" aria-hidden="true" />
            </button>
          </IconTooltip>
          <span className="min-w-2 flex-1" />
          <IconTooltip label={t`Zoom out`}>
            <button
              type="button"
              className={iconButton}
              aria-label={t`Zoom out`}
              disabled={zoomIndex === 0}
              onClick={() => setZoomIndex((i) => Math.max(0, i - 1))}
            >
              <Minus className="size-4" aria-hidden="true" />
            </button>
          </IconTooltip>
          <span className="w-11 shrink-0 text-center text-[12px] tabular-nums text-muted-foreground">
            {Math.round(zoom * 100)} %
          </span>
          <IconTooltip label={t`Zoom in`}>
            <button
              type="button"
              className={iconButton}
              aria-label={t`Zoom in`}
              disabled={zoomIndex === ZOOM_STEPS.length - 1}
              onClick={() => setZoomIndex((i) => Math.min(ZOOM_STEPS.length - 1, i + 1))}
            >
              <Plus className="size-4" aria-hidden="true" />
            </button>
          </IconTooltip>
          <span className="mx-1 h-5 w-px shrink-0 bg-border" aria-hidden="true" />
          <Button
            size="sm"
            className="shrink-0 gap-1.5"
            disabled={chosen.length === 0 || preparing}
            onClick={() => void prepare()}
          >
            {t({ message: "Apply", context: "markup: send marks to the bot" })}
            <span className="rounded-full bg-background/20 px-1.5 text-[11px] tabular-nums">
              {chosen.length}
            </span>
          </Button>
        </div>
      </div>

      {status ? (
        <p
          role="status"
          className="shrink-0 border-b border-border bg-muted/50 px-4 py-2 text-[13px] text-muted-foreground"
        >
          {status}
        </p>
      ) : null}

      <div ref={viewRef} className="min-h-0 flex-1 overflow-auto bg-muted/40 py-4">
        <div className="flex flex-col items-center" style={{ gap: PAGE_GAP_PX }}>
          {fitWidth > 0
            ? pages.map((_, index) => {
                const page = index + 1;
                const size = pageSize(page);
                const anchor =
                  editing?.kind === "draft" && editing.draft.page === page
                    ? commentAnchor(editing.draft.shape)
                    : editing?.kind === "mark"
                      ? anchorOf(split.current, editing.id, page)
                      : null;
                return (
                  <div key={page} className="relative">
                    <MarkupPage
                      page={page}
                      size={size}
                      marks={marksFor(page)}
                      tool={editing ? "select" : tool}
                      color={shapeColor(tool)}
                      drawPage={phase.pdf.draw}
                      onCanvas={onCanvas}
                      onDraw={onDraw}
                      onPick={pick}
                    />
                    {anchor && editing ? (
                      <CommentPopover
                        anchor={anchor}
                        size={size}
                        editing={editing}
                        mark={
                          editing.kind === "mark"
                            ? split.current.find((a) => a.id === editing.id)
                            : undefined
                        }
                        label={
                          editing.kind === "mark"
                            ? (labels.get(editing.id) ?? "")
                            : String(split.current.length + 1)
                        }
                        canEdit={
                          editing.kind === "draft" ||
                          split.current.find((a) => a.id === editing.id)?.authorUserId === userId
                        }
                        saving={saving}
                        error={editError}
                        onChange={(comment) =>
                          setEditing(
                            editing.kind === "draft"
                              ? { kind: "draft", draft: { ...editing.draft, comment } }
                              : { ...editing, comment },
                          )
                        }
                        onCancel={() => {
                          setEditing(null);
                          setEditError(null);
                        }}
                        onSave={() => void saveEditing()}
                        onStatus={(id, next) => void setMarkStatus(id, next)}
                        onRemove={(id) => void removeMark(id)}
                      />
                    ) : null}
                  </div>
                );
              })
            : null}
        </div>
      </div>

      <section className="shrink-0 border-t border-border bg-background" aria-label={t`Marks`}>
        <button
          type="button"
          className="flex h-10 w-full items-center gap-2 px-3 text-start text-[13px] font-medium hover:bg-muted/60 focus-visible:outline-2 focus-visible:outline-ring"
          aria-expanded={drawerOpen}
          aria-controls="markup-list"
          onClick={() => setDrawerOpen((o) => !o)}
        >
          <ChevronDown
            className={`size-4 shrink-0 transition-transform motion-reduce:transition-none ${drawerOpen ? "" : "-rotate-90"}`}
            aria-hidden="true"
          />
          {t`Marks`}
          <span className="min-w-0 truncate font-normal text-muted-foreground">
            {summaryText(openCurrent.length, doneCount, split.older.length)}
          </span>
        </button>
        <div id="markup-list" hidden={!drawerOpen} className="max-h-[38vh] overflow-y-auto pb-2">
          {listError ? (
            <p className="px-3 py-2 text-[13px] text-destructive">
              {t`Marks could not be loaded.`}{" "}
              <button type="button" className="underline" onClick={() => void reloadList()}>
                {t`Try again`}
              </button>
            </p>
          ) : null}
          {split.current.length === 0 && split.older.length === 0 && !listError ? (
            <p className="px-3 py-2 text-[13px] text-muted-foreground">
              {t`No marks yet. Choose a tool and draw on the page.`}
            </p>
          ) : null}
          <ul className="m-0 list-none p-0">
            {split.current.map((a) => (
              <MarkRow
                key={a.id}
                mark={a}
                label={labels.get(a.id) ?? ""}
                checked={selected.has(a.id)}
                checkable={a.status === "open"}
                onToggle={() => setSelected((sel) => toggleSelection(sel, a.id))}
                onOpen={() => {
                  scrollToPage(a.page);
                  pick(a.id);
                }}
              />
            ))}
            {split.older.map((a) => (
              <MarkRow key={a.id} mark={a} label="" older checked={false} checkable={false} />
            ))}
          </ul>
        </div>
      </section>

      {prepared ? (
        <AlertDialog
          open
          onOpenChange={(open) => {
            if (!open && !sending) setPrepared(null);
          }}
        >
          <AlertDialogContent className="max-h-[85vh] overflow-y-auto">
            <AlertDialogHeader>
              <AlertDialogTitle>
                {prepared.length === 1
                  ? t`Send 1 mark to ${sender}?`
                  : t`Send ${prepared.length} marks to ${sender}?`}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t`${sender} changes “${name}” and adds a new version. The excerpt and the text under each mark go along.`}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <ul className="m-0 flex list-none flex-col gap-3 p-0">
              {prepared.map((p) => (
                <li
                  key={p.annotationId}
                  className="grid grid-cols-[20px_88px_1fr] items-start gap-2.5"
                >
                  <span className="grid size-5 place-items-center rounded-full bg-foreground text-[11px] font-semibold text-background">
                    {p.label}
                  </span>
                  {p.png ? (
                    <img
                      src={`data:image/png;base64,${p.png}`}
                      alt={t`Excerpt of mark ${p.label}`}
                      className="max-h-16 w-[88px] rounded border border-border bg-white object-contain"
                    />
                  ) : (
                    <span className="grid h-12 w-[88px] place-items-center rounded border border-dashed border-border text-[11px] text-muted-foreground">
                      {t`No image`}
                    </span>
                  )}
                  <div className="min-w-0 text-[13.5px] [overflow-wrap:anywhere]">
                    {p.comment || <span className="text-muted-foreground">{t`(no comment)`}</span>}
                    {p.text ? (
                      <div className="mt-1 line-clamp-3 text-[12.5px] text-muted-foreground">
                        „{p.text}“
                      </div>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
            {sendError ? (
              <p role="alert" className="text-[13.5px] text-destructive">
                {sendError}
              </p>
            ) : null}
            <AlertDialogFooter>
              <AlertDialogCancel disabled={sending}>{t`Cancel`}</AlertDialogCancel>
              <Button disabled={sending} onClick={() => void send()}>
                {sending ? t`Sending…` : t`Send to ${sender}`}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      ) : null}
    </div>
  );
}

/** Spoken name of a markup colour; the hex code alone means nothing to a screen reader. */
function colourLabel(c: string): string {
  switch (c) {
    case "#e5484d":
      return t`Red`;
    case "#f5a524":
      return t`Orange`;
    case "#3e63dd":
      return t`Blue`;
    case "#30a46c":
      return t`Green`;
    default:
      return c;
  }
}

function summaryText(open: number, done: number, older: number): string {
  const parts = [t`${open} open`];
  if (done > 0) parts.push(t`${done} done`);
  if (older > 0) parts.push(t`${older} from earlier versions`);
  return parts.join(" · ");
}

function anchorOf(current: readonly Annotation[], id: string, page: number) {
  const mark = current.find((a) => a.id === id);
  return mark && mark.page === page ? commentAnchor(mark.shape) : null;
}

function Centered({ children }: { children: ReactNode }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 bg-background p-6">
      {children}
    </div>
  );
}

function kindName(shape: AnnotationShape): string {
  return toolLabel(shape.kind);
}

function MarkRow({
  mark,
  label,
  checked,
  checkable,
  older = false,
  onToggle,
  onOpen,
}: {
  mark: Annotation;
  label: string;
  checked: boolean;
  checkable: boolean;
  older?: boolean;
  onToggle?: () => void;
  onOpen?: () => void;
}) {
  const done = mark.status === "done";
  const detail = older
    ? `${kindName(mark.shape)} · ${t`Version ${mark.version}`} · ${done ? t`done` : t`open`}`
    : done
      ? `${kindName(mark.shape)} · ${t`done`}`
      : kindName(mark.shape);
  return (
    <li
      className={`grid grid-cols-[28px_22px_1fr_auto] items-start gap-2 px-3 py-1.5 ${older || done ? "text-muted-foreground" : ""}`}
    >
      {checkable ? (
        <input
          type="checkbox"
          className="mt-0.5 size-4 justify-self-center accent-foreground"
          checked={checked}
          onChange={onToggle}
          aria-label={t`Include mark ${label} in Apply`}
        />
      ) : (
        <span aria-hidden="true" />
      )}
      <span
        className={`mt-0.5 grid size-5 place-items-center rounded-full text-[11px] font-semibold ${done ? "bg-[#30a46c] text-white" : older ? "bg-muted text-muted-foreground" : "bg-foreground text-background"}`}
        aria-hidden="true"
      >
        {done ? <Check className="size-3" /> : label}
      </span>
      {onOpen ? (
        <button
          type="button"
          className="min-w-0 rounded text-start text-[13.5px] [overflow-wrap:anywhere] hover:underline focus-visible:outline-2 focus-visible:outline-ring"
          onClick={onOpen}
        >
          {mark.comment || t`(no comment)`}
          <small className="block text-[12px] text-muted-foreground">{detail}</small>
        </button>
      ) : (
        <div className="min-w-0 text-[13.5px] [overflow-wrap:anywhere]">
          {mark.comment || t`(no comment)`}
          <small className="block text-[12px]">{detail}</small>
        </div>
      )}
      <span className="whitespace-nowrap text-[12px] tabular-nums text-muted-foreground">
        {t`p. ${mark.page}`}
      </span>
    </li>
  );
}

function CommentPopover({
  anchor,
  size,
  editing,
  mark,
  label,
  canEdit,
  saving,
  error,
  onChange,
  onCancel,
  onSave,
  onStatus,
  onRemove,
}: {
  anchor: [number, number];
  size: { width: number; height: number };
  editing: Editing;
  mark?: Annotation;
  label: string;
  canEdit: boolean;
  saving: boolean;
  error: string | null;
  onChange: (comment: string) => void;
  onCancel: () => void;
  onSave: () => void;
  onStatus: (id: string, next: "open" | "done") => void;
  onRemove: (id: string) => void;
}) {
  const width = Math.min(280, Math.max(220, size.width - 16));
  const left = Math.max(8, Math.min(anchor[0] * size.width, size.width - width - 8));
  const top = Math.min(anchor[1] * size.height + 8, Math.max(8, size.height - 180));
  const comment = editing.kind === "draft" ? editing.draft.comment : editing.comment;
  const shape = editing.kind === "draft" ? editing.draft.shape : mark?.shape;
  const page = editing.kind === "draft" ? editing.draft.page : mark?.page;
  const area = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    area.current?.focus();
  }, []);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      event.stopPropagation();
      onCancel();
    }
    if (event.key === "Enter" && (event.metaKey || event.ctrlKey) && canEdit) {
      event.preventDefault();
      onSave();
    }
  };

  return (
    <div
      role="dialog"
      aria-label={t`Comment on mark ${label}`}
      className="absolute z-20 flex flex-col gap-2 rounded-xl border border-border bg-background p-2.5 shadow-lg"
      style={{ left, top, width }}
      onKeyDown={onKeyDown}
    >
      <div className="flex items-center justify-between gap-2 text-[12px] text-muted-foreground">
        <span className="truncate">
          {label} · {shape ? kindName(shape) : ""} · {t`p. ${page ?? ""}`}
        </span>
        <span className="tabular-nums">
          {utf16Length(comment)}/{ANNOTATION_COMMENT_MAX_LENGTH}
        </span>
      </div>
      <textarea
        ref={area}
        className="min-h-[72px] w-full resize-y rounded-md border border-input bg-background px-2 py-1.5 text-[13.5px] focus-visible:outline-2 focus-visible:outline-ring read-only:bg-muted/40"
        value={comment}
        readOnly={!canEdit}
        maxLength={ANNOTATION_COMMENT_MAX_LENGTH}
        placeholder={t`What should change here?`}
        aria-label={t`Comment`}
        onChange={(e) => onChange(clipUtf16(e.target.value, ANNOTATION_COMMENT_MAX_LENGTH))}
      />
      {error ? (
        <p role="alert" className="text-[12.5px] text-destructive">
          {error}
        </p>
      ) : null}
      <div className="flex flex-wrap items-center justify-end gap-1.5">
        {mark && canEdit ? (
          <Button variant="ghost" size="sm" className="me-auto" onClick={() => onRemove(mark.id)}>
            {t`Delete`}
          </Button>
        ) : null}
        {mark ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onStatus(mark.id, mark.status === "done" ? "open" : "done")}
          >
            {mark.status === "done" ? t`Reopen` : t`Mark done`}
          </Button>
        ) : null}
        <Button variant="outline" size="sm" onClick={onCancel}>
          {editing.kind === "draft" ? t`Discard` : t`Close`}
        </Button>
        {canEdit ? (
          <Button size="sm" disabled={saving} onClick={onSave}>
            {saving ? t`Saving…` : t`Save`}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
